import { useState, useEffect } from 'react';
import { Wind, Sun, Leaf, Droplet, Cpu, Factory, Globe, ArrowRight } from 'lucide-react';

export default function Sector() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  
  const sectors = [
    {
      id: "agriculture",
      icon: <Leaf className="w-12 h-12 text-green-600" />,
      title: "Agriculture & Agri-tech",
      description: "Precision farming techniques, sustainable crop management, and smart irrigation technologies shared between regions.",
      color: "bg-green-500",
      gradient: "from-green-50 to-green-200",
      highlightGradient: "from-green-400 to-green-600"
    },
    {
      id: "solar",
      icon: <Sun className="w-12 h-12 text-yellow-600" />,
      title: "Solar Energy",
      description: "Implementation of advanced solar panel farms and photovoltaic technologies to harness abundant sunlight in Andhra Pradesh.",
      color: "bg-yellow-500",
      gradient: "from-yellow-50 to-yellow-200",
      highlightGradient: "from-yellow-400 to-yellow-600"
    },
    {
      id: "wind",
      icon: <Wind className="w-12 h-12 text-blue-600" />,
      title: "Wind Energy",
      description: "Utilizing Dutch expertise in wind turbine technology along Andhra Pradesh's extensive coastline.",
      color: "bg-blue-500",
      gradient: "from-blue-50 to-blue-200",
      highlightGradient: "from-blue-400 to-blue-600"
    },
    {
      id: "hydrogen",
      icon: <Droplet className="w-12 h-12 text-cyan-600" />,
      title: "Green Hydrogen",
      description: "Collaborative infrastructure for green hydrogen production and distribution systems.",
      color: "bg-cyan-500",
      gradient: "from-cyan-50 to-cyan-200",
      highlightGradient: "from-cyan-400 to-cyan-600"
    },
    {
      id: "tech",
      icon: <Cpu className="w-12 h-12 text-purple-600" />,
      title: "Technology Transfer",
      description: "Knowledge exchange and implementation of innovative technologies from the Netherlands to enhance Andhra Pradesh's infrastructure.",
      color: "bg-purple-500",
      gradient: "from-purple-50 to-purple-200",
      highlightGradient: "from-purple-400 to-purple-600"
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setActiveIndex((prevIndex) => (prevIndex + 1) % sectors.length);
        setIsAnimating(false);
      }, 300);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [sectors.length]);

  return (
    <div className="max-w-6xl mx-auto bg-white rounded-xl shadow-xl overflow-hidden">
      {/* Header Section */}
      <div className="relative overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-green-600 text-white p-8 relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-3 text-center">Sector Specific Initiatives</h2>
          <p className="text-lg md:text-xl max-w-3xl mx-auto text-center">
            <span className="font-semibold">Synergy between Andhra Pradesh and the Netherlands</span>, 
            showcasing collaboration in <span className="font-semibold">Agriculture, Agri-tech, and Green Energy</span>.
          </p>
        </div>
        
        {/* Decorative Elements */}
        <div className="absolute top-0 left-0 w-full h-full opacity-10 z-0">
          <div className="absolute top-4 left-4">
            <Wind className="w-16 h-16" />
          </div>
          <div className="absolute bottom-4 right-4">
            <Sun className="w-16 h-16" />
          </div>
          <div className="absolute top-1/2 left-1/3">
            <Leaf className="w-12 h-12" />
          </div>
          <div className="absolute bottom-1/3 right-1/4">
            <Droplet className="w-14 h-14" />
          </div>
        </div>
      </div>

      {/* Main Interactive Content */}
      <div className="p-6 md:p-8">
        {/* Selector Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {sectors.map((sector, index) => (
            <button
              key={sector.id}
              onClick={() => setActiveIndex(index)}
              className={`flex items-center px-4 py-2 rounded-full transition-all duration-300 ${
                activeIndex === index 
                  ? `text-white bg-gradient-to-r ${sector.highlightGradient} shadow-md` 
                  : `text-gray-700 bg-gray-100 hover:bg-gray-200`
              }`}
            >
              <div className="mr-2">{sector.icon}</div>
              <span className="font-medium">{sector.title}</span>
            </button>
          ))}
        </div>

        {/* Content Display */}
        <div className={`transition-opacity duration-300 ${isAnimating ? 'opacity-0' : 'opacity-100'}`}>
          <div className={`rounded-xl p-6 bg-gradient-to-br ${sectors[activeIndex].gradient} shadow-lg mb-6 transform transition-all duration-500`}>
            <div className="flex flex-col md:flex-row items-center gap-8">
              {/* Left Side - Icon and Title */}
              <div className="md:w-1/3 flex flex-col items-center text-center">
                <div className={`p-6 rounded-full bg-white shadow-md mb-4`}>
                  {sectors[activeIndex].icon}
                </div>
                <h3 className="text-2xl font-bold text-gray-800">{sectors[activeIndex].title}</h3>
              </div>
              
              {/* Right Side - Description and Visual */}
              <div className="md:w-2/3">
                <p className="text-lg mb-6">{sectors[activeIndex].description}</p>
                
                <div className="bg-white p-6 rounded-lg shadow">
                  {/* This would be an image in a real implementation */}
                  <div className="bg-gray-100 h-56 rounded-lg flex items-center justify-center relative overflow-hidden">
                    <div className="text-center z-10">
                      <p className="text-gray-500 font-medium mb-2">
                        {sectors[activeIndex].title} Collaboration
                      </p>
                      <p className="text-sm text-gray-400">
                        Interactive visualization of {sectors[activeIndex].title.toLowerCase()} initiatives
                      </p>
                    </div>
                    
                    {/* Abstract representation of the sector */}
                    <div className="absolute inset-0 opacity-20">
                      {[...Array(8)].map((_, i) => (
                        <div 
                          key={i}
                          className={`absolute ${sectors[activeIndex].color} rounded-full`}
                          style={{
                            width: `${Math.random() * 60 + 20}px`,
                            height: `${Math.random() * 60 + 20}px`,
                            left: `${Math.random() * 100}%`,
                            top: `${Math.random() * 100}%`,
                            opacity: Math.random() * 0.7 + 0.3
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Summary Section */}
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-6 rounded-xl shadow mt-6">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="mb-4 md:mb-0">
              <h3 className="text-xl font-bold text-gray-800 mb-2 flex items-center">
                <Globe className="w-6 h-6 mr-2 text-blue-600" />
                Shared Innovation, Sustainability, and Growth
              </h3>
              <p className="text-gray-600">
                The partnership integrates elements like precision agriculture, green hydrogen infrastructure, 
                solar panels, wind turbines, and iconic landmarks from both regions.
              </p>
            </div>
            <div>
              <button className="bg-gradient-to-r from-blue-600 to-green-600 text-white px-6 py-3 rounded-lg shadow-md flex items-center hover:shadow-lg transition-all">
                Explore Initiatives <ArrowRight className="ml-2 w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}